import http from '@/api/http'
import { subscriptionTypesPageLimit } from '@/config/api'
import { cursorParams, type PageMeta } from '@/api/pagination'

export interface SubscriptionTypePrice {
  // Latest catalog price record nested inside a `SubscriptionType`.

  id: string
  // Price record id (UUID7).

  start: string
  // ISO datetime from which this price is effective.

  price: number
  // Price amount in major currency units.
}

export interface SubscriptionType {
  // One subscription plan from GET `/subscription-types` (a package of visits for a period).

  id: string
  // Subscription type id (UUID7).

  name: string
  // Human-readable plan name (e.g. "Month · bachata").

  number_of_services: number | null
  // Maximum number of covered visits; `null` means unlimited.

  duration: number
  // Validity period in days.

  price: SubscriptionTypePrice | null
  // Latest catalog price; null when none is set.

  business_service_type_ids: string[]
  // Service types the plan covers (ids; resolve names via the service types catalog).
}

interface SubscriptionTypesResponse extends PageMeta {
  // Paginated list payload from GET `/subscription-types`.

  items: SubscriptionType[]
  // Page of subscription types.
}

/** A plan is unlimited when it has no visit cap; the table shows "—" in the visits column then. */
export function isUnlimitedSubscriptionType(type: Pick<SubscriptionType, 'number_of_services'>) {
  return type.number_of_services === null
}

/**
 * Fetches a page of subscription types.
 * @param cursor - Optional; pass `next_cursor` from a previous page for the next page.
 */
export async function fetchSubscriptionTypes(cursor?: number): Promise<SubscriptionTypesResponse> {
  const { data } = await http.get<SubscriptionTypesResponse>('/subscription-types', {
    params: cursorParams(subscriptionTypesPageLimit, cursor),
  })
  if (import.meta.env.DEV) {
    console.log('[fetchSubscriptionTypes] GET /subscription-types response', data)
  }
  return { ...data, items: data.items ?? [] }
}

export interface SubscriptionTypePricePayload {
  // Catalog price sent on create/update; a new price record starts at `start`.

  start: string
  // ISO 8601 datetime from which the price is effective.

  price: number
  // Price amount in major currency units.
}

export interface CreateSubscriptionTypePayload {
  // Request body for POST `/subscription-types`.

  name: string
  number_of_services: number | null
  // Visit cap; `null` creates an unlimited plan.

  duration: number
  // Validity in days (>= 1).

  price: SubscriptionTypePricePayload
  business_service_type_ids: string[]
  // Covered service types; at least one.
}

export interface UpdateSubscriptionTypePayload {
  // Request body for PATCH `/subscription-types/{id}`; omitted fields stay unchanged.

  name?: string
  number_of_services?: number | null
  // `null` makes the plan unlimited.

  duration?: number
  price?: SubscriptionTypePricePayload
  // Adds a new price record (history is kept).

  business_service_type_ids?: string[]
  // Replacement list of covered service types (at least one).
}

/** Fetches one subscription type by id (used by the info card). */
export async function fetchSubscriptionType(id: string): Promise<SubscriptionType> {
  const { data } = await http.get<SubscriptionType>(`/subscription-types/${id}`)
  if (import.meta.env.DEV) {
    console.log(`[fetchSubscriptionType] GET /subscription-types/${id} response`, data)
  }
  return data
}

/** Creates a subscription type via POST `/subscription-types`. */
export async function createSubscriptionType(
  payload: CreateSubscriptionTypePayload,
): Promise<SubscriptionType> {
  const { data } = await http.post<SubscriptionType>('/subscription-types', payload)
  if (import.meta.env.DEV) {
    console.log('[createSubscriptionType] POST /subscription-types response', data)
  }
  return data
}

/** Updates a subscription type via PATCH `/subscription-types/{id}`. */
export async function updateSubscriptionType(
  id: string,
  payload: UpdateSubscriptionTypePayload,
): Promise<SubscriptionType> {
  const { data } = await http.patch<SubscriptionType>(`/subscription-types/${id}`, payload)
  if (import.meta.env.DEV) {
    console.log(`[updateSubscriptionType] PATCH /subscription-types/${id} response`, data)
  }
  return data
}

/** Deletes a subscription type via DELETE `/subscription-types/{id}`. */
export async function deleteSubscriptionType(id: string): Promise<void> {
  await http.delete(`/subscription-types/${id}`)
  if (import.meta.env.DEV) {
    console.log(`[deleteSubscriptionType] DELETE /subscription-types/${id}`)
  }
}
